// ============================================
// Dr. C Simple Review Card
//
// Dead-simple: one card, one question, three buttons.
// "Does the model's assessment look clinically sound?"
// YES | MOSTLY | NO + optional comment
//
// Dr. C is NOT a technical participant.
// 60 seconds max per case.
// ============================================

import { useState } from 'react';
import { CheckCircle, AlertTriangle, XCircle } from 'lucide-react';
import type { CalibrationCase, DrCReview as DrCReviewType } from '../../../types/labUpload';
import { updateCalibrationCase } from '../../../services/firestore/calibrationCaseService';
import { recordConvergence, shouldGenerateDivergenceSummary } from '../../../services/calibration/convergenceService';
import { generateDivergenceSummary } from '../../../services/calibration/divergenceSummary';

interface DrCReviewProps {
  calibrationCase: CalibrationCase;
  onReviewComplete: () => void;
}

export function DrCReview({ calibrationCase, onReviewComplete }: DrCReviewProps) {
  const [rating, setRating] = useState<'yes' | 'mostly' | 'no' | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const blindRun = calibrationCase.blindRunOutput;
  const drC = calibrationCase.drCAssessment;
  const divergences = (calibrationCase.decisionPoints ?? []).filter(
    (dp) => dp.alignment === 'delta',
  );

  async function handleSubmit() {
    if (!rating) return;
    setError(null);
    setSubmitting(true);

    try {
      const review: DrCReviewType = {
        rating,
        comment: comment.trim() || undefined,
        reviewedAt: new Date().toISOString(),
      };

      await updateCalibrationCase(calibrationCase.caseId, {
        drCReview: review,
        status: 'reviewed',
      });

      // Record convergence data point
      const divergenceAreas = (calibrationCase.decisionPoints ?? [])
        .filter((dp) => dp.alignment === 'delta')
        .map((dp) => dp.area);

      await recordConvergence(
        calibrationCase.caseId,
        review,
        calibrationCase.alignmentScore ?? 0,
        divergenceAreas,
      );

      // Check if it's time to generate a divergence summary (every 25 reviews)
      // This is the feedback loop: disagreements → divergence patterns → prompt improvements
      try {
        const shouldGenerate = await shouldGenerateDivergenceSummary();
        if (shouldGenerate) {
          // Fire-and-forget: don't block the review flow
          generateDivergenceSummary(calibrationCase.brandId).catch((err) =>
            console.error('[DrCReview] Divergence summary generation failed:', err),
          );
        }
      } catch (err) {
        // Non-critical — don't block review completion
        console.error('[DrCReview] Divergence check failed:', err);
      }

      onReviewComplete();
    } catch (err) {
      console.error('[DrCReview] Submit failed:', err);
      setError('Failed to submit review. Please try again.');
      setSubmitting(false);
    }
  }

  const ratingButtons: { value: 'yes' | 'mostly' | 'no'; label: string; icon: typeof CheckCircle; color: string }[] = [
    { value: 'yes', label: 'Yes', icon: CheckCircle, color: 'bg-green-600 hover:bg-green-700 text-white' },
    { value: 'mostly', label: 'Mostly', icon: AlertTriangle, color: 'bg-amber-500 hover:bg-amber-600 text-white' },
    { value: 'no', label: 'No', icon: XCircle, color: 'bg-red-600 hover:bg-red-700 text-white' },
  ];

  return (
    <div className="bg-white rounded border border-zinc-200 p-6 max-w-2xl mx-auto">
      {/* Header */}
      <h3 className="font-['Instrument_Serif'] text-xl text-zinc-900 mb-4">
        Case #{calibrationCase.caseId.slice(-6)} — Lab Analysis Review
      </h3>

      {/* Model Assessment */}
      {blindRun && (
        <div className="mb-4">
          <p className="text-sm font-semibold text-zinc-600 mb-1">Model Assessment:</p>
          <p className="text-base text-zinc-800 bg-zinc-50 rounded p-3 border border-zinc-100">
            {blindRun.diagnosis}
          </p>
        </div>
      )}

      {/* Dr. C Assessment */}
      {drC && (
        <div className="mb-4">
          <p className="text-sm font-semibold text-zinc-600 mb-1">Your Assessment (from SOAP):</p>
          <p className="text-base text-zinc-800 bg-zinc-50 rounded p-3 border border-zinc-100">
            {drC.diagnosis}
          </p>
        </div>
      )}

      {/* Key Divergences */}
      {divergences.length > 0 && (
        <div className="mb-5">
          <p className="text-sm font-semibold text-zinc-600 mb-1">Key Divergences:</p>
          <ul className="space-y-1">
            {divergences.map((d, i) => (
              <li key={i} className="text-sm text-zinc-700 bg-amber-50 rounded p-2 border border-amber-100">
                <span className="font-medium">{d.area}:</span> {d.deltaNote}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* The Question */}
      <p className="text-base font-semibold text-zinc-900 mb-3">
        Does the model&apos;s assessment look clinically sound?
      </p>

      {/* Rating Buttons */}
      <div className="flex gap-3 mb-4">
        {ratingButtons.map(({ value, label, icon: Icon, color }) => (
          <button
            key={value}
            onClick={() => setRating(value)}
            className={`flex items-center gap-2 px-6 py-3 rounded text-base font-semibold transition-all ${
              rating === value
                ? `${color} ring-2 ring-rose-500 ring-offset-2`
                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
            }`}
          >
            <Icon size={20} />
            {label}
          </button>
        ))}
      </div>

      {/* Optional Comment */}
      <div className="mb-4">
        <label className="text-sm text-zinc-500 block mb-1">Optional comment:</label>
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Any notes on what the model got right or wrong..."
          className="w-full border border-zinc-200 rounded p-3 text-sm text-zinc-800 resize-none h-20 focus:outline-none focus:ring-1 focus:ring-rose-500"
        />
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={!rating || submitting}
        className="w-full py-3 rounded text-base font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-rose-600 text-white hover:bg-rose-700"
      >
        {submitting ? 'Submitting...' : 'Submit Review'}
      </button>
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
}
